/**
 * Conversation limit ("modo cola") — capacity status and user-facing texts.
 * Pure module: no app imports, trivially testable.
 */

export const CAPACITY_STATUS = Object.freeze({
  AVAILABLE: 'available',
  NEAR_LIMIT: 'near_limit',
  SATURATED: 'saturated',
  EXCEEDED: 'exceeded',
});

export const CAPACITY_STATUS_VALUES = Object.values(CAPACITY_STATUS);

export const STATUS_LABELS = Object.freeze({
  [CAPACITY_STATUS.AVAILABLE]: 'Disponible',
  [CAPACITY_STATUS.NEAR_LIMIT]: 'Cerca del límite',
  [CAPACITY_STATUS.SATURATED]: 'Lleno',
  [CAPACITY_STATUS.EXCEEDED]: 'Excedido',
});

const isPositiveInt = (value) => Number.isInteger(value) && value > 0;

/**
 * Same thresholds as the backend (spec §5): available <80 %, near_limit
 * 80–99 %, saturated =100 %, exceeded >100 %. Integer math, no float drift.
 */
export const deriveStatus = (activeCount, limit) => {
  if (!isPositiveInt(limit) || !Number.isInteger(activeCount) || activeCount < 0) {
    return CAPACITY_STATUS.AVAILABLE;
  }
  if (activeCount > limit) return CAPACITY_STATUS.EXCEEDED;
  if (activeCount === limit) return CAPACITY_STATUS.SATURATED;
  if (activeCount * 5 >= limit * 4) return CAPACITY_STATUS.NEAR_LIMIT;
  return CAPACITY_STATUS.AVAILABLE;
};

/** "3 de 5 conversaciones" — empty string when there is nothing to show. */
export const formatCapacity = (capacity) => {
  if (!capacity || !isPositiveInt(capacity.limit) || !Number.isInteger(capacity.activeCount)) {
    return '';
  }
  const noun = capacity.limit === 1 ? 'conversación' : 'conversaciones';
  return `${capacity.activeCount} de ${capacity.limit} ${noun}`;
};

/** Release reasons accepted by POST /conversation-limit/release (spec §5). */
export const RELEASE_REASONS = Object.freeze([
  { value: 'NO_CONTESTA', label: 'No contesta' },
  { value: 'NO_ES_MI_ZONA', label: 'No es mi zona' },
  { value: 'PRODUCTO_INCORRECTO', label: 'Producto incorrecto' },
  { value: 'ERROR_DE_ASIGNACION', label: 'Error de asignación' },
  { value: 'DUPLICADO', label: 'Duplicado' },
  { value: 'OTRO', label: 'Otro' },
]);

export const RELEASE_REASON_VALUES = RELEASE_REASONS.map((r) => r.value);

export const RELEASE_COMMENT_MAX = 500;

/** Reasons carried by the `conversationUnseated` Pusher event (spec §6). */
export const UNSEAT_REASON_LABELS = Object.freeze({
  closed: 'se cerró',
  released: 'se liberó',
  reassigned: 'se reasignó a otro agente',
  idle: 'pasó demasiado tiempo sin actividad',
  swept: 'ya no está en tu cupo',
});

export const unseatReasonLabel = (reason) =>
  (typeof reason === 'string' && UNSEAT_REASON_LABELS[reason]) || 'ya no está en tu cupo';
